import { FC, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ReactComponent as CloseIcon } from '../../../assets/icons/Close.svg';
import { ReactComponent as MemberIcon } from '../../../assets/icons/UserRounded.svg';
import './Modal.scss';

type ModalProps = {
  isOpen: boolean;
  onClose: () => void;
  children: any;
  title?: string;
  width?: string;
  /** When set, modal uses fixed height and content area scrolls; other modals are unchanged */
  height?: string;
  memberName?: string;
  backgroundColor?: string;
  titleAction?: React.ReactNode;
  closeOnOverlayClick?: boolean;
  closeOnEsc?: boolean;
  wrapperClassName?: string;
  isFullscreen?: boolean;
};

const Modal: FC<ModalProps> = ({
  isOpen,
  onClose,
  children,
  title,
  width,
  height,
  memberName,
  backgroundColor,
  titleAction,
  closeOnOverlayClick = false,
  closeOnEsc = false,
  wrapperClassName,
  isFullscreen = false,
}) => {
  useEffect(() => {
    if (!isOpen) return;

    const { body } = document;
    const currentCount = Number(body.dataset.modalOpenCount || '0');
    body.dataset.modalOpenCount = String(currentCount + 1);
    body.classList.add('modal-open');

    return () => {
      const nextCount = Math.max(
        0,
        Number(body.dataset.modalOpenCount || '1') - 1
      );

      if (nextCount === 0) {
        body.classList.remove('modal-open');
        delete body.dataset.modalOpenCount;
      } else {
        body.dataset.modalOpenCount = String(nextCount);
      }
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !closeOnEsc) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, closeOnEsc, onClose]);

  if (!isOpen) return null;

  return createPortal(
    <div
      className={['overlay', isFullscreen ? 'overlay--fullscreen' : ''].filter(Boolean).join(' ')}
      onClick={closeOnOverlayClick ? onClose : undefined}
    >
      <div
        className={[
          'modalWrapper',
          isFullscreen ? 'modalWrapper--fullscreen' : '',
          wrapperClassName,
        ].filter(Boolean).join(' ')}
        onClick={(event) => event.stopPropagation()}
      >
        <div
          className={[
            'modal',
            height || isFullscreen ? 'modal--fixed-height' : '',
            isFullscreen ? 'modal--fullscreen' : '',
          ].filter(Boolean).join(' ')}
          style={isFullscreen ? { backgroundColor } : { width, height, backgroundColor }}
        >
          {(title || titleAction || memberName) && (
            <div className='title'>
              <div className='title-left'>
                {titleAction && <div className='title-action'>{titleAction}</div>}
                <div>{title ?? ''}</div>
              </div>
              {memberName && (
                <div className='member-name'>
                  <MemberIcon />
                  {memberName}
                </div>
              )}
            </div>
          )}
          <div className='content'>{children}</div>
        </div>

        <div className='closeButton' onClick={onClose}>
          <CloseIcon />
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default Modal;
